import { CheckCircleIcon } from '@heroicons/react/24/outline';

/**
 * The summary card used across the invoicing reports — a figure, its label, and the
 * definition on hover, optionally acting as a filter for the table beneath it.
 *
 * Extracted from Invoice Reconciliation when Invoice Overrides needed the same card row.
 * Shared rather than copied on purpose: the two reports sit next to each other in the
 * menu and answer adjacent questions, so a card meaning "click me to filter" has to look
 * identical on both. A second copy would have drifted on the first tweak to either.
 */

/** Per-card colour. Follows the At Risk Monitor's language: a left accent bar over a
 *  tinted fill, and an inset ring when the card is the active filter. */
export type CardTone =
  | 'red' | 'amber' | 'purple' | 'blue' | 'slate' | 'green' | 'teal' | 'indigo';

export const CARD_TONES: Record<CardTone, {
  idle: string; selected: string; value: string; bar: string;
}> = {
  red: {
    idle: 'border-red-200 bg-gradient-to-br from-red-50 to-white hover:from-red-100 dark:border-red-800 dark:from-red-950/60 dark:to-gray-900',
    selected: 'bg-red-600 border-red-600 text-white shadow-md ring-2 ring-offset-1 ring-red-700 dark:ring-offset-gray-900',
    value: 'text-red-700 dark:text-red-300',
    bar: 'bg-red-500',
  },
  amber: {
    idle: 'border-yellow-300 bg-gradient-to-br from-yellow-50 to-white hover:from-yellow-100 dark:border-yellow-700 dark:from-yellow-950/60 dark:to-gray-900',
    selected: 'bg-yellow-500 border-yellow-500 text-white shadow-md ring-2 ring-offset-1 ring-yellow-600 dark:ring-offset-gray-900',
    value: 'text-yellow-700 dark:text-yellow-300',
    bar: 'bg-yellow-400',
  },
  purple: {
    idle: 'border-purple-200 bg-gradient-to-br from-purple-50 to-white hover:from-purple-100 dark:border-purple-800 dark:from-purple-950/60 dark:to-gray-900',
    selected: 'bg-purple-600 border-purple-600 text-white shadow-md ring-2 ring-offset-1 ring-purple-700 dark:ring-offset-gray-900',
    value: 'text-purple-700 dark:text-purple-300',
    bar: 'bg-purple-500',
  },
  blue: {
    idle: 'border-sky-200 bg-gradient-to-br from-sky-50 to-white hover:from-sky-100 dark:border-sky-800 dark:from-sky-950/60 dark:to-gray-900',
    selected: 'bg-sky-600 border-sky-600 text-white shadow-md ring-2 ring-offset-1 ring-sky-700 dark:ring-offset-gray-900',
    value: 'text-sky-700 dark:text-sky-300',
    bar: 'bg-sky-500',
  },
  slate: {
    idle: 'border-gray-200 bg-gradient-to-br from-gray-100 to-white hover:from-gray-200 dark:border-gray-700 dark:from-gray-800 dark:to-gray-900',
    selected: 'bg-gray-600 border-gray-600 text-white shadow-md ring-2 ring-offset-1 ring-gray-700 dark:ring-offset-gray-900',
    value: 'text-gray-700 dark:text-gray-300',
    bar: 'bg-gray-400',
  },
  green: {
    idle: 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white hover:from-emerald-100 dark:border-emerald-800 dark:from-emerald-950/60 dark:to-gray-900',
    selected: 'bg-emerald-600 border-emerald-600 text-white shadow-md ring-2 ring-offset-1 ring-emerald-700 dark:ring-offset-gray-900',
    value: 'text-emerald-700 dark:text-emerald-300',
    bar: 'bg-emerald-500',
  },
  teal: {
    idle: 'border-teal-200 bg-gradient-to-br from-teal-50 to-white hover:from-teal-100 dark:border-teal-800 dark:from-teal-950/60 dark:to-gray-900',
    selected: 'bg-teal-600 border-teal-600 text-white shadow-md ring-2 ring-offset-1 ring-teal-700 dark:ring-offset-gray-900',
    value: 'text-teal-700 dark:text-teal-300',
    bar: 'bg-teal-500',
  },
  indigo: {
    idle: 'border-indigo-200 bg-gradient-to-br from-indigo-50 to-white hover:from-indigo-100 dark:border-indigo-800 dark:from-indigo-950/60 dark:to-gray-900',
    selected: 'bg-indigo-600 border-indigo-600 text-white shadow-md ring-2 ring-offset-1 ring-indigo-700 dark:ring-offset-gray-900',
    value: 'text-indigo-700 dark:text-indigo-300',
    bar: 'bg-indigo-500',
  },
};

export interface ReportTileProps {
  label: string;
  value: string;
  /** Shown on hover of the whole card — the definition, and nothing else. */
  help: string;
  tone?: CardTone;
  /** The one figure the page is about: solid fill rather than a tint. */
  hero?: boolean;
  /** Present => the card filters the report when clicked. */
  onClick?: () => void;
  active?: boolean;
  /** A second line under the figure — a denominator, a caveat, a count. */
  footnote?: string;
}

export default function ReportTile({
  label, value, help, tone = 'slate', hero, onClick, active, footnote,
}: ReportTileProps) {
  const t = CARD_TONES[tone];
  // The whole card is the hover target, so the definition needs no (i) to hang off, and
  // `title` stays put whether or not the card is selected — a selected card is the one
  // you are most likely to want the definition for.
  // SOLID MEANS SELECTED. One rule, no exceptions — the hero used to be solid red while
  // unselected, so a selected Missing card and an unselected Actionable card were two
  // near-identical red blocks side by side. Rank is carried by SIZE instead: the hero's
  // figure is larger and its accent bar thicker, which survives being selected.
  //
  // idle and selected are also MUTUALLY EXCLUSIVE, never concatenated: the idle style is
  // a gradient, a gradient is a background IMAGE, and it paints straight over the solid
  // `bg-*-600` that selection sets — which left a selected card pale with white text on it.
  const cls = [
    'relative overflow-hidden rounded-lg border pr-3 py-2 text-left transition',
    hero ? 'pl-4' : 'pl-3',
    active ? t.selected : t.idle,
    onClick ? 'cursor-pointer hover:shadow-md' : '',
  ].join(' ');
  const body = (
    <>
      {!active && (
        <span className={`absolute inset-y-0 left-0 ${hero ? 'w-1.5' : 'w-1'} ${t.bar}`}
              aria-hidden="true" />
      )}
      <div className={`text-[10px] uppercase tracking-wide truncate flex items-center gap-1 ${
        hero ? 'font-semibold' : 'font-medium'
      } ${active ? 'text-white/90' : 'text-gray-600 dark:text-gray-300'}`}>
        {active && <CheckCircleIcon className="w-3 h-3 shrink-0" aria-hidden="true" />}
        <span className="truncate">{label}</span>
      </div>
      <div className={`font-bold font-mono leading-tight ${hero ? 'text-2xl' : 'text-lg'} ${
        active ? 'text-white' : t.value
      }`}>
        {value}
      </div>
      {footnote && (
        <div className={`text-[10px] leading-tight truncate ${
          active ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
        }`}>
          {footnote}
        </div>
      )}
    </>
  );

  if (!onClick) return <div className={cls} title={help}>{body}</div>;
  return (
    <button type="button" onClick={onClick} aria-pressed={!!active} title={help} className={cls}>
      {body}
    </button>
  );
}
